/**
 * API-Zugriff auf E-Mail-Vorlagen (Textbausteine).
 * Backend: internal/mail/templates, Routen /protected/mail-templates.
 */

import type { MailPlaceholder } from '@/utils/mailTemplate'
import {
  deleteRequestGeneric,
  getRequestGeneric,
  postRequestGeneric,
  putRequestGeneric,
} from '@/services/backendApiGeneric'
import { normalizeCaseType } from '@/utils/mailTemplateFilter'

export type MailTemplateScope = 'organization' | 'company' | 'personal'

/** Fallart einer Vorlage – leer = in allen Fallarten (Kasko-Schreiben: 'kasko'). */
export type MailTemplateCaseType = '' | 'kasko' | 'haftpflicht'

export interface MailTemplate {
  id: number
  idOrganization: number
  idCompany: number | null
  idUser: number | null
  name: string
  category: string
  /** Fallart, in der die Vorlage angeboten wird ("" = alle Fallarten). */
  caseType: MailTemplateCaseType
  subject: string
  bodyHtml: string
  sortOrder: number
  isActive: boolean
  createdBy: number | null
  createdAt: string
  updatedAt: string
  scope: MailTemplateScope
  canEdit: boolean
}

export interface MailTemplateCategory {
  key: string
  label: string
}

export interface MailTemplateListResponse {
  templates: MailTemplate[]
  categories: MailTemplateCategory[]
  /** Wählbare Fallarten (Schlüssel "" = alle Fallarten). */
  caseTypes: MailTemplateCategory[]
  placeholders: MailPlaceholder[]
  canManageShared: boolean
}

export interface MailTemplateInput {
  name: string
  category: string
  caseType?: MailTemplateCaseType
  subject: string
  bodyHtml: string
  scope: MailTemplateScope
  idCompany?: number | null
  sortOrder?: number
  isActive?: boolean
}

export const SCOPE_LABELS: Record<MailTemplateScope, string> = {
  organization: 'Organisation',
  company: 'Unternehmen',
  personal: 'Persönlich',
}

const BASE = '/protected/mail-templates'

/** Ältere Backends liefern keine Fallart – dann gilt „alle Fallarten“. */
function normalizeTemplate (t: MailTemplate): MailTemplate {
  return { ...t, caseType: normalizeCaseType(t.caseType) }
}

export async function listMailTemplates (): Promise<MailTemplateListResponse> {
  const data = await getRequestGeneric(BASE)
  return {
    templates: Array.isArray(data?.templates) ? data.templates.map((t: MailTemplate) => normalizeTemplate(t)) : [],
    categories: Array.isArray(data?.categories) ? data.categories : [],
    caseTypes: Array.isArray(data?.caseTypes) ? data.caseTypes : [],
    placeholders: Array.isArray(data?.placeholders) ? data.placeholders : [],
    canManageShared: data?.canManageShared === true,
  }
}

export function createMailTemplate (input: MailTemplateInput): Promise<MailTemplate> {
  return postRequestGeneric(BASE, input, true)
}

export function updateMailTemplate (id: number, input: MailTemplateInput): Promise<MailTemplate> {
  return putRequestGeneric(`${BASE}/${id}`, input, true)
}

export function deleteMailTemplate (id: number): Promise<{ deleted: boolean, id: number }> {
  return deleteRequestGeneric(`${BASE}/${id}`)
}
